import { globalPointToScreenshotPoint } from "./screenshot-pointer";
import type {
  VoiceAttentionChapterAttachment,
  VoiceAttentionContext,
  VoiceAttentionPoint,
  VoiceAttentionState,
  VoiceAttentionStroke,
} from "./voice-attention-store";

/** A stroke split where it crosses into another chapter, in drawing order. */
export function contiguousChapterSegments(stroke: VoiceAttentionStroke) {
  const segments: Array<{
    chapterId: string;
    displayId: number;
    points: VoiceAttentionPoint[];
  }> = [];
  for (const point of stroke.points) {
    const last = segments[segments.length - 1];
    if (!last || last.chapterId !== point.chapterId) {
      segments.push({
        chapterId: point.chapterId,
        displayId: point.displayId,
        points: [point],
      });
    } else {
      last.points.push(point);
    }
  }
  return segments;
}

export function buildVoiceAttentionContextFromState(
  attachments: VoiceAttentionChapterAttachment[],
  snapshot: VoiceAttentionState,
): VoiceAttentionContext | null {
  if (
    !snapshot.recordingId ||
    !snapshot.activation ||
    snapshot.strokes.length === 0
  ) {
    return null;
  }
  const attachmentByChapter = new Map(
    attachments.map((attachment) => [attachment.chapterId, attachment]),
  );
  const chapterById = new Map(
    snapshot.chapters.map((chapter) => [chapter.id, chapter]),
  );
  const usedChapterIds = new Set<string>();
  const strokes = snapshot.strokes.flatMap((stroke) =>
    contiguousChapterSegments(stroke).flatMap((segment, segmentIndex) => {
      const attachment = attachmentByChapter.get(segment.chapterId);
      const chapter = chapterById.get(segment.chapterId);
      if (!attachment || !chapter) return [];
      const points = segment.points.flatMap((point) => {
        const screenshotPoint = globalPointToScreenshotPoint(
          attachment.screen,
          point,
        );
        return screenshotPoint
          ? [{ t_ms: point.tMs, x: screenshotPoint.x, y: screenshotPoint.y }]
          : [];
      });
      if (points.length === 0) return [];
      usedChapterIds.add(chapter.id);
      const startedMs = points[0]!.t_ms;
      const endedMs = points[points.length - 1]!.t_ms;
      const speechStartMs = Math.max(chapter.openedMs, startedMs - 1_500);
      const speechEndMs = Math.max(
        speechStartMs,
        Math.min(chapter.closedMs ?? endedMs + 2_500, endedMs + 2_500),
      );
      return [
        {
          stroke_id: `${stroke.id}:${segmentIndex + 1}`,
          chapter_id: chapter.id,
          started_ms: startedMs,
          ended_ms: endedMs,
          display_id: segment.displayId,
          screen: attachment.screen.index + 1,
          image_id: attachment.imageId,
          speech_window: { start_ms: speechStartMs, end_ms: speechEndMs },
          points,
        },
      ];
    }),
  );
  if (strokes.length === 0) return null;
  const chapters = snapshot.chapters.flatMap((chapter) => {
    const attachment = attachmentByChapter.get(chapter.id);
    if (!attachment || !usedChapterIds.has(chapter.id)) return [];
    return [
      {
        chapter_id: chapter.id,
        display_id: chapter.displayId,
        revision: chapter.revision,
        trigger: chapter.trigger,
        opened_ms: chapter.openedMs,
        ...(chapter.settledMs === undefined
          ? {}
          : { settled_ms: chapter.settledMs }),
        ...(chapter.closedMs === undefined
          ? {}
          : { closed_ms: chapter.closedMs }),
        screen: attachment.screen.index + 1,
        image_id: attachment.imageId,
      },
    ];
  });
  return {
    version: 2,
    recording_id: snapshot.recordingId,
    activation: snapshot.activation,
    chapters,
    strokes,
    transcript_timeline: snapshot.transcriptTimeline.map((item, index) => ({
      kind: item.kind,
      t_ms: item.tMs,
      text: item.text,
      ...(index === snapshot.transcriptTimeline.length - 1 &&
      item.speechSegments.length > 0
        ? { speech_segments: item.speechSegments }
        : {}),
    })),
  };
}
